import { createApiClient } from '@cortex/api-client';
import { config } from './config.ts';

// The access token lives in memory only (never in localStorage). The accounts
// integration (E1.3) sets it after sign-in and on refresh.
let accessToken: string | undefined;

export const setAccessToken = (token: string | undefined) => {
  accessToken = token;
};

/** The current access token (the chat panel sends it to the assistant service). */
export const getAccessToken = () => accessToken;

/** The API's origin (and path prefix): every client of the web app (the API's, each SDK's) calls it. */
export const apiBaseUrl = new URL(config.VITE_API_BASE_URL, window.location.origin)
  .toString()
  .replace(/\/$/, '');
const baseUrl = apiBaseUrl;

/**
 * The header an app's chat names its app with (a person may reach the project only through the app): the api's
 * APP_REACH_HEADER (@cortex/api-kit names.ts, a server package the browser does not load).
 */
export const APP_REACH_HEADER = 'x-cortex-app';

// The shared app this page shows (its share link, SharedApp.tsx), or null: its companions' pages (Workflows, Models,
// Docs, …) then name it on every call, so a person who reaches the project only through the app reads them there
// (the api admits only the companions the app's owner switched on, and only reads).
let reachingApp: string | null = null;

/** The shared app the page shows; null when it closes. Returns the undo. */
export const setReachingApp = (app: string | null): (() => void) => {
  const before = reachingApp;
  reachingApp = app;
  return () => {
    if (reachingApp === app) reachingApp = before;
  };
};

/** The shared app this page shows, or null (a cortex page): a companion's page leaves out cortex's own affordances. */
export const reachingAppOf = (): string | null => reachingApp;

/** `fetch` naming the shared app the page shows, when there is one: every api client of a companion's page uses it. */
export const apiFetch: typeof fetch = (input, init) => {
  if (!reachingApp) return fetch(input, init);
  const req = new Request(input, init);
  if (!req.headers.has(APP_REACH_HEADER)) req.headers.set(APP_REACH_HEADER, reachingApp);
  return fetch(req);
};

/** The only way the web app calls the API. */
export const api = createApiClient({ baseUrl, getToken: () => accessToken, fetch: apiFetch });
